import { Users, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react'

interface FunnelStage {
  key: string
  label: string
  count: number
  trend?: number
  icon: React.ReactNode
  color: string
  bgColor: string
  filterKey: string
}

interface SubscriptionFunnelProps {
  data: {
    trial: number
    active: number
    past_due: number
    suspended: number
    canceled: number
    trial_expiring_7d: number
  }
  onFilterClick?: (status: string) => void
  loading?: boolean
}

export function SubscriptionFunnel({ data, onFilterClick, loading = false }: SubscriptionFunnelProps) {
  const stages: FunnelStage[] = [
    {
      key: 'trial',
      label: 'Em Trial',
      count: data.trial,
      icon: <Clock className="w-5 h-5" />,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      filterKey: 'trial',
    },
    {
      key: 'active',
      label: 'Ativos',
      count: data.active,
      icon: <CheckCircle className="w-5 h-5" />,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      filterKey: 'active',
    },
    {
      key: 'past_due',
      label: 'Pagamento Atrasado',
      count: data.past_due,
      icon: <AlertTriangle className="w-5 h-5" />,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      filterKey: 'past_due',
    },
    {
      key: 'suspended',
      label: 'Suspensos',
      count: data.suspended,
      icon: <XCircle className="w-5 h-5" />,
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      filterKey: 'suspended',
    },
    {
      key: 'canceled',
      label: 'Cancelados',
      count: data.canceled,
      icon: <XCircle className="w-5 h-5" />,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      filterKey: 'canceled',
    },
  ]

  const total = stages.reduce((sum, stage) => sum + stage.count, 0)

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200">
        <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-100 rounded-lg animate-pulse" />
              <div className="flex-1 h-4 bg-gray-100 rounded animate-pulse" />
              <div className="w-16 h-6 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">Funil de Assinaturas</h3>
        <span className="text-sm text-gray-500">{total} organizações</span>
      </div>

      {/* Trial expiring alert */}
      {data.trial_expiring_7d > 0 && (
        <button
          onClick={() => onFilterClick?.('trial_expiring')}
          className="w-full mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 hover:bg-amber-100 transition-colors"
        >
          <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
            <Clock className="w-4 h-4 text-amber-600" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-amber-800">
              {data.trial_expiring_7d} trial{data.trial_expiring_7d > 1 ? 's' : ''} expirando em 7 dias
            </p>
            <p className="text-xs text-amber-600">Clique para ver detalhes</p>
          </div>
        </button>
      )}

      {/* Funnel stages */}
      <div className="space-y-3">
        {stages.map((stage) => {
          const percentage = total > 0 ? (stage.count / total) * 100 : 0
          return (
            <button
              key={stage.key}
              onClick={() => onFilterClick?.(stage.filterKey)}
              className="w-full flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors group"
            >
              <div className={`w-10 h-10 ${stage.bgColor} rounded-lg flex items-center justify-center ${stage.color}`}>
                {stage.icon}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{stage.label}</span>
                  <span className={`text-sm font-semibold ${stage.color}`}>{stage.count}</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${stage.bgColor.replace('100', '400')}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
              <span className="text-xs text-gray-400 w-12 text-right">{percentage.toFixed(0)}%</span>
            </button>
          )
        })}
      </div>

      {/* View all link */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <a
          href="/super-admin/organizations"
          className="text-sm text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
        >
          <Users className="w-4 h-4" />
          Ver todas as organizações
        </a>
      </div>
    </div>
  )
}
